import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Nav } from '../components/Nav'
import { DISTORTION_INFO } from '../components/DistortionInfo'

export default function Help() {
  const [activeSection, setActiveSection] = useState<string | null>('how-it-works')
  const [expandedDistortion, setExpandedDistortion] = useState<string | null>(null)

  const toggleSection = (section: string) => {
    setActiveSection(activeSection === section ? null : section)
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Help & Learn</h1>
        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
          Understand cognitive reframing
        </p>
      </div>

      <div className="page-content" style={{ paddingBottom: '100px' }}>
        {/* How it works */}
        <div className="card" style={{ marginBottom: '1rem' }}>
          <button
            onClick={() => toggleSection('how-it-works')}
            style={{
              width: '100%',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: 'var(--color-text)'
            }}
          >
            <h3 style={{ margin: 0 }}>How PresentLens Works</h3>
            <span style={{ color: 'var(--color-text-muted)' }}>
              {activeSection === 'how-it-works' ? '−' : '+'}
            </span>
          </button>

          {activeSection === 'how-it-works' && (
            <div style={{ marginTop: '1rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <p style={{
                  fontWeight: '500',
                  color: 'var(--color-primary)',
                  marginBottom: '0.5rem'
                }}>
                  1. Share Your Thoughts
                </p>
                <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                  Speak or type what's bothering you. Describe the situation, how you feel,
                  and what thoughts are going through your mind.
                </p>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <p style={{
                  fontWeight: '500',
                  color: 'var(--color-primary)',
                  marginBottom: '0.5rem'
                }}>
                  2. AI Analysis
                </p>
                <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                  Our AI identifies the trigger, automatic thoughts, emotions, and any
                  cognitive distortions (unhelpful thinking patterns).
                </p>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <p style={{
                  fontWeight: '500',
                  color: 'var(--color-primary)',
                  marginBottom: '0.5rem'
                }}>
                  3. Explore Evidence
                </p>
                <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                  Review evidence both supporting and challenging your automatic thoughts.
                  This helps create distance from the thought.
                </p>
              </div>

              <div>
                <p style={{
                  fontWeight: '500',
                  color: 'var(--color-primary)',
                  marginBottom: '0.5rem'
                }}>
                  4. Find Balance
                </p>
                <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                  Discover a more balanced, realistic perspective and a concrete action
                  step you can take.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* What is CBT */}
        <div className="card" style={{ marginBottom: '1rem' }}>
          <button
            onClick={() => toggleSection('what-is-cbt')}
            style={{
              width: '100%',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: 'var(--color-text)'
            }}
          >
            <h3 style={{ margin: 0 }}>What is Cognitive Reframing?</h3>
            <span style={{ color: 'var(--color-text-muted)' }}>
              {activeSection === 'what-is-cbt' ? '−' : '+'}
            </span>
          </button>

          {activeSection === 'what-is-cbt' && (
            <div style={{ marginTop: '1rem', fontSize: '0.875rem' }}>
              <p style={{ marginBottom: '0.75rem', lineHeight: '1.5' }}>
                Cognitive reframing is a technique from Cognitive Behavioral Therapy (CBT),
                one of the most researched and effective forms of therapy.
              </p>

              <p style={{ marginBottom: '0.75rem', lineHeight: '1.5' }}>
                The core idea is simple: <strong>it's not events that upset us, but our
                interpretation of those events.</strong>
              </p>

              <p style={{ marginBottom: '0.75rem', lineHeight: '1.5' }}>
                When something upsetting happens, we often have automatic thoughts—quick
                interpretations that feel true but may be distorted. Reframing helps us:
              </p>

              <ul style={{
                marginLeft: '1.25rem',
                marginBottom: '0.75rem',
                color: 'var(--color-text-muted)'
              }}>
                <li>Notice our automatic thoughts</li>
                <li>Identify thinking patterns (distortions)</li>
                <li>Examine evidence objectively</li>
                <li>Find a more balanced perspective</li>
              </ul>

              <div style={{
                padding: '0.75rem',
                backgroundColor: 'var(--color-bg)',
                borderRadius: '0.5rem',
                borderLeft: '3px solid var(--color-primary)'
              }}>
                <p style={{ color: 'var(--color-text)', fontStyle: 'italic' }}>
                  "We are disturbed not by things, but by the views which we take of them."
                </p>
                <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                  — Epictetus (1st century AD)
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Cognitive Distortions */}
        <div className="card" style={{ marginBottom: '1rem' }}>
          <button
            onClick={() => toggleSection('distortions')}
            style={{
              width: '100%',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: 'var(--color-text)'
            }}
          >
            <h3 style={{ margin: 0 }}>12 Cognitive Distortions</h3>
            <span style={{ color: 'var(--color-text-muted)' }}>
              {activeSection === 'distortions' ? '−' : '+'}
            </span>
          </button>

          {activeSection === 'distortions' && (
            <div style={{ marginTop: '1rem' }}>
              <p style={{
                fontSize: '0.875rem',
                color: 'var(--color-text-muted)',
                marginBottom: '1rem'
              }}>
                These common thinking patterns can make us feel worse than necessary.
                Recognizing them is the first step to changing them.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {Object.entries(DISTORTION_INFO).map(([key, info]) => (
                  <div key={key}>
                    <button
                      onClick={() => setExpandedDistortion(
                        expandedDistortion === key ? null : key
                      )}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: 'var(--color-bg)',
                        border: expandedDistortion === key
                          ? '1px solid var(--color-primary)'
                          : '1px solid var(--color-border)',
                        borderRadius: '0.5rem',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        color: 'var(--color-text)',
                        textAlign: 'left'
                      }}
                    >
                      <span style={{ fontWeight: '500' }}>{info.name}</span>
                      <span style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>
                        {expandedDistortion === key ? '▲' : '▼'}
                      </span>
                    </button>

                    {expandedDistortion === key && (
                      <div style={{
                        marginTop: '0.5rem',
                        padding: '0.75rem',
                        backgroundColor: 'var(--color-bg)',
                        borderRadius: '0.5rem',
                        borderLeft: '3px solid var(--color-primary)',
                        fontSize: '0.8rem'
                      }}>
                        <p style={{
                          color: 'var(--color-text)',
                          marginBottom: '0.5rem',
                          lineHeight: '1.4'
                        }}>
                          {info.description}
                        </p>
                        <p style={{
                          color: 'var(--color-text-muted)',
                          fontStyle: 'italic',
                          marginBottom: '0.5rem'
                        }}>
                          {info.example}
                        </p>
                        <p style={{
                          color: 'var(--color-success)',
                          fontSize: '0.75rem'
                        }}>
                          Reframe: {info.reframe}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Tips for success */}
        <div className="card" style={{ marginBottom: '1rem' }}>
          <button
            onClick={() => toggleSection('tips')}
            style={{
              width: '100%',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: 'var(--color-text)'
            }}
          >
            <h3 style={{ margin: 0 }}>Tips for Effective Reframing</h3>
            <span style={{ color: 'var(--color-text-muted)' }}>
              {activeSection === 'tips' ? '−' : '+'}
            </span>
          </button>

          {activeSection === 'tips' && (
            <div style={{ marginTop: '1rem', fontSize: '0.875rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <p style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                  Be specific
                </p>
                <p style={{ color: 'var(--color-text-muted)' }}>
                  Instead of "I feel bad," describe exactly what happened and how you felt.
                </p>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <p style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                  Look for the "hot thought"
                </p>
                <p style={{ color: 'var(--color-text-muted)' }}>
                  The thought that triggers the strongest emotional response is usually
                  the one worth reframing.
                </p>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <p style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                  Be honest about evidence
                </p>
                <p style={{ color: 'var(--color-text-muted)' }}>
                  Really consider evidence on both sides. The goal isn't positive thinking,
                  it's realistic thinking.
                </p>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <p style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                  Practice regularly
                </p>
                <p style={{ color: 'var(--color-text-muted)' }}>
                  Like any skill, reframing gets easier with practice. Try to do at least
                  one session when you notice difficult emotions.
                </p>
              </div>

              <div>
                <p style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                  Take the action step
                </p>
                <p style={{ color: 'var(--color-text-muted)' }}>
                  Thoughts matter, but so do actions. Following through on your action
                  step reinforces the new perspective.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Important disclaimer */}
        <div style={{
          padding: '1rem',
          backgroundColor: 'var(--color-bg-light)',
          borderRadius: '0.5rem',
          borderLeft: '3px solid var(--color-warning)',
          marginBottom: '1rem'
        }}>
          <p style={{
            fontWeight: '500',
            color: 'var(--color-warning)',
            marginBottom: '0.5rem'
          }}>
            Important
          </p>
          <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
            PresentLens is a self-help tool, not a substitute for professional mental health care.
            If you're struggling with persistent anxiety, depression, or other mental health concerns,
            please reach out to a licensed therapist or counselor.
          </p>
        </div>

        {/* Crisis link */}
        <Link
          to="/crisis"
          style={{
            display: 'block',
            padding: '1rem',
            backgroundColor: 'var(--color-bg-light)',
            borderRadius: '0.5rem',
            textAlign: 'center',
            textDecoration: 'none',
            color: 'var(--color-error)'
          }}
        >
          In crisis? Get immediate help →
        </Link>
      </div>

      <Nav />
    </div>
  )
}
